/**
 * Zod schema + TypeScript types for the data-driven dialogue JSON format.
 *
 * Dialogue files live at: packages/client/public/dialogue/{id}.json
 *
 * Simple linear dialogue (no choices):
 * ```json
 * {
 *   "npcName": "Guard",
 *   "nodes": [
 *     { "id": "0", "text": "Halt! Who goes there?" },
 *     { "id": "1", "text": "Move along, traveler." }
 *   ]
 * }
 * ```
 *
 * Branching dialogue with choices:
 * ```json
 * {
 *   "npcName": "Innkeeper",
 *   "nodes": [
 *     {
 *       "id": "start",
 *       "text": "Welcome! What can I do for you?",
 *       "choices": [
 *         { "text": "Tell me about the ruins.", "next": "ruins" },
 *         { "text": "Just passing through.", "next": "end" }
 *       ]
 *     },
 *     {
 *       "id": "ruins",
 *       "text": "Strange lights at night... I'd stay away if I were you.",
 *       "next": "end"
 *     }
 *   ]
 * }
 * ```
 *
 * Node traversal rules:
 * - If a node has `choices`, the player must click one — no auto-advance.
 * - If a node has `next`, advance to that node id after the player continues.
 * - If neither, advance to the next node in the array; if last, close dialogue.
 * - A `next` of `"end"` always closes the dialogue.
 * - A choice `next` of `"end"` or `""` also closes the dialogue.
 */

import { z } from 'zod';

export const DialogueChoiceSchema = z.object({
  /** Button label shown to the player. */
  text: z.string().min(1),
  /** Node id to navigate to, or "end" / "" to close dialogue. */
  next: z.string(),
});

export const DialogueNodeSchema = z.object({
  /** Unique id within this dialogue (used by choices and `next` references). */
  id: z.string().min(1),
  /** Text spoken by the NPC. */
  text: z.string().min(1),
  /**
   * Explicit next node id after the player continues.
   * Omit to advance linearly; set to "end" to close after this node.
   */
  next: z.string().optional(),
  /**
   * If present, the typewriter completes then these choice buttons appear.
   * The player must click a choice; keyboard advance is ignored.
   */
  choices: z.array(DialogueChoiceSchema).optional(),
});

export const DialogueDataSchema = z.object({
  /** Optional id matching the filename (e.g. "innkeeper_greeting"). */
  id: z.string().optional(),
  /** Display name shown above the dialogue text. */
  npcName: z.string().min(1),
  /**
   * Portrait asset key (for future sprite lookup).
   * Currently unused visually — a coloured placeholder is rendered instead.
   */
  portrait: z.string().optional(),
  /** Ordered list of nodes. First node is always shown first. */
  nodes: z.array(DialogueNodeSchema).min(1),
});

export type DialogueChoice = z.infer<typeof DialogueChoiceSchema>;
export type DialogueNode = z.infer<typeof DialogueNodeSchema>;
export type DialogueData = z.infer<typeof DialogueDataSchema>;
